import { useCallback, useEffect, useMemo, useState } from "react";
import { apiClient } from "../../../api/apiClient";
import EditModal from "../modals/EditModal";

const orgTypeToCouncilType = {
  student: "STUDENT_COUNCIL",
  emergency: "EMERGENCY_COMMITTEE",
  acting: "ACTING",
};

const getPastArray = (result) => {
  const data = result?.data;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.contents)) return data.contents;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.list)) return data.list;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.content)) return data.data.content;

  return [];
};

const normalizePastCouncil = (item) => ({
  id: item.pastCouncilId ?? item.id,
  gen: item.termNumber || "",
  name: item.councilName || "",
  councilType: item.councilType || "STUDENT_COUNCIL",
  president: item.president || "",
  vicePresident: item.vicePresident || "",
  reportUrl: item.reportUrl || "",
  members:
    item.councilType === "ACTING"
      ? "공석"
      : `총: ${item.president || "-"}, 부총: ${item.vicePresident || "-"}`,
});

function PastIntro({ orgType }) {
  const [pastList, setPastList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const currentCouncilType = orgTypeToCouncilType[orgType] || "STUDENT_COUNCIL";

  const fetchPastCouncils = useCallback(async () => {
    try {
      setLoading(true);

      const result = await apiClient("/api/council/past?page=0&size=100");
      const list = getPastArray(result).map(normalizePastCouncil);

      setPastList(list);
    } catch (error) {
      console.error("역대 총학생회 조회 오류:", error);
      alert(error.message || "역대 총학생회 조회 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPastCouncils();
  }, [fetchPastCouncils]);

  const currentList = useMemo(() => {
    return pastList.filter((item) => item.councilType === currentCouncilType);
  }, [pastList, currentCouncilType]);

  const handleAddClick = () => {
    setSelectedItem(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (item) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm("삭제하시겠습니까?");
    if (!confirmDelete) return;

    try {
      await apiClient(`/api/council/past/${id}`, {
        method: "DELETE",
      });

      setPastList((prev) => prev.filter((item) => item.id !== id));
      alert("삭제되었습니다.");
    } catch (error) {
      console.error("역대 총학생회 삭제 오류:", error);
      alert(error.message || "삭제 중 오류가 발생했습니다.");
    }
  };

  const handleSave = async (form) => {
    const requestBody = {
      termNumber: form.gen,
      councilName: form.name,
      president: form.president,
      vicePresident: form.vicePresident,
      reportUrl: form.reportUrl || selectedItem?.reportUrl || "",
      councilType: selectedItem?.councilType || currentCouncilType,
    };

    try {
      if (selectedItem) {
        const result = await apiClient(`/api/council/past/${selectedItem.id}`, {
          method: "PUT",
          body: JSON.stringify(requestBody),
        });

        const updatedData = normalizePastCouncil(result.data);

        setPastList((prev) =>
          prev.map((item) => (item.id === selectedItem.id ? updatedData : item))
        );

        alert("수정되었습니다.");
      } else {
        const result = await apiClient("/api/council/past", {
          method: "POST",
          body: JSON.stringify(requestBody),
        });

        const newData = normalizePastCouncil(result.data);

        setPastList((prev) => [newData, ...prev]);

        alert("등록되었습니다.");
      }

      handleCloseModal();
    } catch (error) {
      console.error("역대 총학생회 저장 오류:", error);
      alert(error.message || "저장 중 오류가 발생했습니다.");
    }
  };

  return (
    <>
      <div className="past-intro-toolbar">
        <button
          className="past-intro-add-btn"
          type="button"
          onClick={handleAddClick}
          disabled={loading}
          aria-label="역대 총학생회 추가"
        >
          +
        </button>
      </div>

      <div className="table-wrap intro-table-wrap admin-scroll-x">
        <div className="table-header intro-table-header">
          <div>대수</div>
          <div>이름</div>
          <div>구성원</div>
          <div>관리</div>
        </div>

        {loading ? (
          <div className="table-empty-row">불러오는 중입니다.</div>
        ) : currentList.length > 0 ? (
          currentList.map((item) => (
            <div key={item.id} className="table-row intro-table-row">
              <div>{item.gen}</div>
              <div>{item.name}</div>
              <div>{orgType === "acting" ? "공석" : item.members}</div>

              <div className="table-actions">
                {orgType !== "acting" && (
                  <button
                    className="edit"
                    onClick={() => handleEditClick(item)}
                    type="button"
                  >
                    수정
                  </button>
                )}

                <button
                  className="delete"
                  onClick={() => handleDelete(item.id)}
                  type="button"
                >
                  삭제
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="table-empty-row">등록된 내역이 없습니다.</div>
        )}
      </div>

      <EditModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        data={selectedItem}
        onSave={handleSave}
      />
    </>
  );
}

export default PastIntro;